import { call, put, select, takeEvery, takeLatest, take } from 'redux-saga/effects';
import uuidv4 from 'uuid/v4';
import uploadingAssetProvider from 'JS/shared/providers/UploadingAssetProvider';
import { parseSvgWidthAndHeight } from 'JS/shared/helpers/parseSvgWidthAndHeight';

import track from '../shared/helpers/tracking';
import {
  SEARCH_IMAGE,
  searchImagesSuccess,
  searchImagesFailure,
  ADD_USER_UPLOAD_IMAGE_TO_CANVAS,
  GET_IMAGE_LIBRARIES,
  getImageLibrariesSuccess,
  getImageLibrariesFailure,
  GET_IMAGE_LIBRARY,
  getImageLibrarySuccess,
  getImageLibraryFailure,
  GET_USER_IMAGES_REQUESTED,
  getUserImagesFailure,
  getUserImagesSuccess,
  ADD_USER_IMAGE_TO_SCRIBE,
  DELETE_USER_IMAGE_REQUESTED,
  deleteUserImageSuccess,
  deleteUserImageFailure,
  ADD_LIBRARY_IMAGE_TO_CANVAS
} from '../actionCreators/imagesActions';
import {
  uploadLibraryImageAsset,
  uploadUserImageAsset,
  UPDATE_SCRIBE_SUCCESS,
  createImageElement
} from '../actionCreators/scribeActions';
import { startLoading, stopLoading, showError, hideModal } from '../actionCreators/uiActions';
import { appServices } from '../shared/helpers/app-services/AppServices';
import { getImageDimensions } from '../shared/helpers/image';
import { getProviderForType } from '../shared/providers';
import { getImageLibraries, getImageLibrary, searchImageLibrary } from '../shared/providers/ImageLibrary';
import { fitElementToCanvas } from '../shared/helpers/element';
import { rateLimitedfetch } from '../shared/helpers/requestLimiter';
import { sendErrorToSentry } from '../logging';

import { getToken } from './selectors';

export function* addUserUploadImageToCanvas(action) {
  const { scribeId, file, filename, canvasSize } = action;
  yield put(startLoading());
  try {
    if (!action.file) throw new Error('No file supplied to saga');
    const { type: imageType } = file;

    let fileToUpload;

    if (imageType === 'image/svg+xml') {
      const blob = yield parseSvgWidthAndHeight(file);
      fileToUpload = blob;
    } else {
      fileToUpload = file;
    }

    const urlCreator = window.URL || window.webkitURL;
    let objectUrl = urlCreator.createObjectURL(fileToUpload);

    const temporaryElementId = uuidv4();
    const temporaryAssetId = `temp-${uuidv4()}`;
    uploadingAssetProvider.save(temporaryAssetId, objectUrl);

    const { scaleX, scaleY, x, y, width, height } = yield call(fitImageToCanvas, objectUrl, imageType, canvasSize);

    yield put(
      createImageElement(scribeId, canvasSize, {
        id: temporaryElementId,
        image: { contentType: imageType, provider: 'upload', filename, assetId: temporaryAssetId },
        scaleX,
        scaleY,
        x,
        y,
        width,
        height,
        _imageUrl: objectUrl
      })
    );

    yield put(stopLoading());
    yield put(hideModal(action.scribeId));

    let count = 10;

    while (true) {
      const successScribe = yield take(UPDATE_SCRIBE_SUCCESS);

      if (successScribe.scribe.projectModel.elements.find(element => element.id === temporaryElementId)) {
        yield put(uploadUserImageAsset(fileToUpload, action.filename, action.scribeId, temporaryAssetId));
        return;
      }
      count--;
      if (count === 0) return;
    }
  } catch (error) {
    console.error(error);
    sendErrorToSentry(error);
  } finally {
    yield put(stopLoading());
    yield put(hideModal(action.scribeId));
  }
}

export function* getUserImages() {
  try {
    const assets = yield call(appServices.getAssetsList);
    const images = assets
      .filter(asset => /^image/.test(asset.contentType))
      .map(image => {
        return {
          ...image,
          id: image.projectAssetId
        };
      });
    yield put(getUserImagesSuccess(images));
  } catch (error) {
    sendErrorToSentry(error);
    yield put(getUserImagesFailure(error));
    yield put(showError(error));
  }
}

function* fitImageToCanvas(url, contentType, canvasSize) {
  const imgDimensions = yield call(getImageDimensions, url, contentType);
  return {
    ...fitElementToCanvas(imgDimensions.width, imgDimensions.height, canvasSize),
    width: imgDimensions.width,
    height: imgDimensions.height
  };
}

export function* addLibraryImageToCanvas(action) {
  try {
    yield put(startLoading());
    const provider = getProviderForType(action.provider);
    const url = yield call(provider, action.imageId);
    const resp = yield rateLimitedfetch(url);
    const blob = yield resp.blob();

    const { scaleX, scaleY, x, y, width, height } = yield call(
      fitImageToCanvas,
      url,
      action.imageType,
      action.canvasSize
    );

    const elementId = uuidv4();

    const urlCreator = window.URL || window.webkitURL;
    let objectUrl = urlCreator.createObjectURL(blob);

    yield put(
      createImageElement(action.scribeId, action.canvasSize, {
        id: elementId,
        image: {
          contentType: action.imageType,
          provider: action.provider,
          filename: action.filename,
          assetId: action.imageId
        },
        scaleX,
        scaleY,
        x,
        y,
        width,
        height,
        _imageUrl: objectUrl
      })
    );

    yield put(hideModal(action.scribeId));
    yield put(stopLoading());

    let count = 10;

    while (true) {
      const successScribe = yield take(UPDATE_SCRIBE_SUCCESS);

      if (successScribe.scribe.projectModel.elements.find(element => element.id === elementId)) {
        yield put(uploadLibraryImageAsset(blob, action.filename, action.scribeId, action.imageId));
        return;
      }
      count--;
      if (count === 0) return;
    }
  } catch (error) {
    console.error(error);
    sendErrorToSentry(error);
    yield put(showError(error));
  }
  yield put(stopLoading());
}

export function* addUserImageToScribe(action) {
  let assetId = action.imageId;
  const provider = getProviderForType('user');
  try {
    yield put(startLoading());
    const url = yield call(provider, assetId);

    const { scaleX, scaleY, x, y, width, height } = yield call(
      fitImageToCanvas,
      url,
      action.imageType,
      action.canvasSize
    );

    const signedImgUrl = yield call(provider, assetId);

    yield put(
      createImageElement(action.scribeId, action.canvasSize, {
        image: {
          contentType: action.imageType,
          provider: action.provider,
          filename: action.filename,
          assetId: action.imageId
        },
        scaleX,
        scaleY,
        x,
        y,
        width,
        height,
        _imageUrl: signedImgUrl
      })
    );

    yield put(hideModal(action.scribeId));
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
  }
  yield put(stopLoading());
}

export function* deleteUserImage(action) {
  try {
    yield call(appServices.deleteAsset, action.imageId);
    yield put(deleteUserImageSuccess(action.imageId));
  } catch (error) {
    sendErrorToSentry(error);
    yield put(deleteUserImageFailure(error));
    yield put(showError(error));
  }
}

function* handleGetImageLibraries() {
  try {
    const token = yield select(state => state.auth && state.auth.currentUser && state.auth.currentUser.token);
    const libraries = yield call(getImageLibraries, token);
    yield put(getImageLibrariesSuccess(libraries.data));
  } catch (e) {
    sendErrorToSentry(e);
    yield put(getImageLibrariesFailure(e));
  }
}

function* handleGetImageLibrary(action) {
  try {
    const { libraryId, cursor } = action;

    const token = yield select(getToken);
    const library = yield call(getImageLibrary, libraryId, token, cursor);
    yield put(getImageLibrarySuccess(libraryId, library.images, library.cursor));
  } catch (e) {
    sendErrorToSentry(e);
    yield put(getImageLibraryFailure(e));
  }
}

function* handleSearchImage(action) {
  try {
    const { query, offset } = action;
    const token = yield select(getToken);
    const results = yield call(searchImageLibrary, query, token, offset);

    if (offset) {
      track.event({
        category: 'Image',
        action: 'IMAGE_SEARCH_OFFSET',
        label: query,
        value: offset
      });
    } else {
      track.event({
        category: 'Image',
        action: 'IMAGE_SEARCH',
        label: query,
        value: results && results.meta ? parseInt(results.meta.total, 10) : 0
      });
    }
    yield put(searchImagesSuccess(results.data.length ? results.data[0].images : [], results.meta.total));
  } catch (e) {
    sendErrorToSentry(e);
    yield put(searchImagesFailure(e));
  }
}

function* imageSagas() {
  yield takeEvery(ADD_USER_UPLOAD_IMAGE_TO_CANVAS, addUserUploadImageToCanvas);
  yield takeEvery(GET_USER_IMAGES_REQUESTED, getUserImages);
  yield takeEvery(ADD_USER_IMAGE_TO_SCRIBE, addUserImageToScribe);
  yield takeEvery(DELETE_USER_IMAGE_REQUESTED, deleteUserImage);
  yield takeLatest(GET_IMAGE_LIBRARIES, handleGetImageLibraries);
  yield takeEvery(GET_IMAGE_LIBRARY, handleGetImageLibrary);
  yield takeEvery(SEARCH_IMAGE, handleSearchImage);
  yield takeEvery(ADD_LIBRARY_IMAGE_TO_CANVAS, addLibraryImageToCanvas);
}

export default imageSagas;
